import React, { useEffect, useRef, useState } from "react";
import { FallingLines } from "react-loader-spinner";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Switch from "react-switch";
import { toast } from "react-toastify";
import CameraIcon from "../../assets/svg/CameraIcon.svg";
import RightArrow from "../../assets/svg/RightArrow";
import Header from "../../components/Header/Header";
import ScrollToTop from "../../components/Hooks/ScrollToTop";
import axios from "../../components/Hooks/axios";
import { logOut, setUserObject, switchAccount } from "../../redux/slices/authSlice";
import { setNotificationSound } from "../../redux/slices/notificationSlice";
import styles from "./Setting.module.css";
import SwitchAccount from "./SwitchAccount/SwitchAccount";
import Avatar from "../../components/Avatar/Avatar";
import { removeUser } from "../../redux/slices/accountSlice";
import close from "../../assets/images/closeSettings.png";
import { toggleTheme } from "../../redux/slices/themeSlice";
import defaultProfileImg from "../../assets/images/user.png";
import SocialLinksSection from "./SocialLinks/SocialLinksSection";
import notificationSoundFile from "../../assets/sound/mixkit-clear-announce-tones-2861.wav";

const Setting = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { userId, user } = useSelector((state) => state.auth);
	const { users } = useSelector((state) => state.account);
	const [isLoading, setIsLoading] = useState(false);
	const ImageRef = useRef(null);
	const [ActiveStatus, setActiveStatus] = useState(true);
	const [EnableLocation, setEnableLocation] = useState(false);
	const [PlaySoundNotification, setPlaySoundNotification] = useState(false);
	const [DesktopNotification, setDesktopNotification] = useState(false);
	const [browserNotifPermission, setBrowserNotifPermission] = useState(
		typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
	);
	const [bioText, setBioText] = useState("");
	const [editBio, setEditBio] = useState(false);
	const [addAccountPopup, setAddAccountPopup] = useState(false);



	useEffect(() => {
		if (userId === "") return;

		setActiveStatus(Boolean(user?.is_active));
		setEnableLocation(Boolean(user?.enable_location));
		setPlaySoundNotification(Boolean(user?.notification_sound));
		dispatch(setNotificationSound(Boolean(user?.notification_sound)));
		setDesktopNotification(Boolean(user?.desktop_notification));
		setBioText(user?.bio || "");

		axios
			.get(`/auth/get-user-details/${userId}`)
			.then(({ data }) => {
				dispatch(setUserObject(data));
				setBioText(data.bio || "");
				if (data.enable_location !== undefined) setEnableLocation(Boolean(data.enable_location));
				if (data.notification_sound !== undefined) {
					setPlaySoundNotification(Boolean(data.notification_sound));
					dispatch(setNotificationSound(Boolean(data.notification_sound)));
				}
				if (data.desktop_notification !== undefined) setDesktopNotification(Boolean(data.desktop_notification));
			})
			.catch((e) => {
				console.log(e);
			});
	}, [userId]);

	const handelActiveSwitch = (e) => {
		const id = toast.loading("Please Wait..");
		axios
			.get(`/auth/make-active-inactive?status=${e}`)
			.then(({ data }) => {
				setActiveStatus(e);
				dispatch(setUserObject(data.data));
				toast.update(id, {
					render: "Your Active Status Change",
					type: "success",
					isLoading: false,
					autoClose: 2000,
				});
			})
			.catch((e) => {
				console.log(e);
			});
	};

	const handleNotificationSound = (val) => {
		const id = toast.loading("Updating notification sound...");
		axios
			.post(`/auth/update-user-privacy`, {
				privacy_type: "notification_sound",
				value: val,
			})
			.then(({ data }) => {
				setPlaySoundNotification(val);
				dispatch(setNotificationSound(val));
				dispatch(setUserObject(data.data));
				if (val) {
					try {
						const audio = new Audio(notificationSoundFile);
						audio.play().catch((e) => console.log("Audio preview error:", e));
					} catch (e) {
						console.log(e);
					}
				}
				toast.update(id, {
					render: val ? "Notification sound enabled" : "Notification sound disabled",
					type: "success",
					isLoading: false,
					autoClose: 2000,
				});
			})
			.catch((e) => {
				console.error(e);
				toast.update(id, {
					render: "Failed to update notification sound",
					type: "error",
					isLoading: false,
					autoClose: 2000,
				});
			});
	};

	const testDesktopNotification = () => {
		if (typeof window === "undefined" || !("Notification" in window)) {
			return toast.warn("Desktop notifications are not supported in this browser.");
		}
		if (Notification.permission !== "granted") {
			return toast.warn(`Browser permission is "${Notification.permission}". Please enable it first.`);
		}

		if (PlaySoundNotification) {
			try {
				const audio = new Audio(notificationSoundFile);
				audio.play().catch((e) => console.log(e));
			} catch (e) {}
		}

		// On-screen popup notification
		toast.info(
			<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
				<img src="/favicon.ico" alt="" style={{ width: "32px", height: "32px", borderRadius: "50%" }} />
				<div>
					<div style={{ fontWeight: 600, fontSize: "14px" }}>Fameo Notification Test</div>
					<div style={{ fontSize: "12px", opacity: 0.9 }}>If you see this, popup notifications are working!</div>
				</div>
			</div>,
			{ position: "top-right", autoClose: 4000 }
		);

		try {
			const notif = new Notification("Fameo Desktop Notification Test", {
				body: "Desktop notifications are working properly! You will receive alerts from Fameo.",
				icon: "/favicon.ico",
			});
			notif.onclick = () => {
				window.focus();
				notif.close();
			};
			notif.onerror = (err) => {
				console.error("Test notification display error:", err);
				toast.error("Notification banner blocked by OS. Check Windows Focus Assist or Notification settings.");
			};
		} catch (e) {
			console.error("Notification constructor error:", e);
			toast.error(`Error displaying notification: ${e.message}`);
		}
	};

	const handleDesktopNotification = async (val) => {
		if (!val) {
			const id = toast.loading("Disabling desktop notifications...");
			try {
				const { data } = await axios.post(`/auth/update-user-privacy`, {
					privacy_type: "desktop_notification",
					value: false,
				});
				setDesktopNotification(false);
				dispatch(setUserObject(data.data));
				toast.update(id, {
					render: "Desktop notifications disabled",
					type: "success",
					isLoading: false,
					autoClose: 2000,
				});
			} catch (e) {
				console.error(e);
				toast.update(id, {
					render: "Failed to update desktop notification setting",
					type: "error",
					isLoading: false,
					autoClose: 2000,
				});
			}
			return;
		}

		if (typeof window === "undefined" || !("Notification" in window)) {
			return toast.warn("This browser does not support desktop notifications.");
		}

		let currentPerm = Notification.permission;

		if (currentPerm === "denied") {
			setBrowserNotifPermission("denied");
			return toast.error(
				"Notifications are blocked by your browser for this site. Click the lock/tune icon near your URL bar and switch Notifications to 'Allow', then try again.",
				{ autoClose: 7000 }
			);
		}

		if (currentPerm === "default") {
			try {
				let permResult;
				try {
					permResult = await Notification.requestPermission();
				} catch (promiseErr) {
					permResult = await new Promise((resolve) => Notification.requestPermission((p) => resolve(p)));
				}
				currentPerm = permResult || Notification.permission;
				setBrowserNotifPermission(currentPerm);
			} catch (err) {
				console.error("Error requesting notification permission:", err);
			}
		}

		if (currentPerm !== "granted") {
			setDesktopNotification(false);
			setBrowserNotifPermission(currentPerm);
			if (currentPerm === "denied") {
				return toast.error("Notification permission was denied. Please allow notifications in site settings.");
			}
			return toast.warn("Notification permission was not granted.");
		}

		setBrowserNotifPermission("granted");
		const id = toast.loading("Enabling desktop notifications...");
		try {
			const { data } = await axios.post(`/auth/update-user-privacy`, {
				privacy_type: "desktop_notification",
				value: true,
			});
			setDesktopNotification(true);
			dispatch(setUserObject(data.data));

			// Play sound if enabled
			if (PlaySoundNotification) {
				try {
					const audio = new Audio(notificationSoundFile);
					audio.play().catch((e) => console.log(e));
				} catch (e) {}
			}

			// On-screen popup notification
			toast.info(
				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					<img src="/favicon.ico" alt="" style={{ width: "32px", height: "32px", borderRadius: "50%" }} />
					<div>
						<div style={{ fontWeight: 600, fontSize: "14px" }}>Fameo Notifications</div>
						<div style={{ fontSize: "12px", opacity: 0.9 }}>Desktop & on-screen notifications are now active!</div>
					</div>
				</div>,
				{ position: "top-right", autoClose: 4000 }
			);

			try {
				const testNotif = new Notification("Fameo Notifications Enabled", {
					body: "You will now receive desktop notifications from Fameo!",
					icon: "/favicon.ico",
				});
				testNotif.onclick = () => {
					window.focus();
					testNotif.close();
				};
				testNotif.onerror = (err) => {
					console.warn("Desktop notification banner error (check Windows Focus Assist / notifications):", err);
				};
			} catch (notifErr) {
				console.log("Desktop notification constructor warning:", notifErr);
			}

			toast.update(id, {
				render: "Desktop notifications enabled!",
				type: "success",
				isLoading: false,
				autoClose: 2000,
			});
		} catch (e) {
			console.error(e);
			toast.update(id, {
				render: "Failed to update desktop notification setting",
				type: "error",
				isLoading: false,
				autoClose: 2000,
			});
		}
	};

	const handelProfileImageChange = (e) => {
		let file = e.target.files[0];
		if (file) {
			setIsLoading(true);
			const id = toast.loading("Picture Uploading !");
			const formData = new FormData();
			formData.append("ProfilePic", file);
			axios
				.post(`/auth/upload-profile-picture?userId=${user?._id}`, formData)
				.then(({ data }) => {
					if (data.success) {
						setIsLoading(false);
						toast.update(id, {
							render: "Profile Picture Uploaded Successfully !",
							type: "success",
							isLoading: false,
							autoClose: 2000,
						});
						dispatch(setUserObject(data.data));
					}
				})
				.catch((e) => {
					console.log(e);
				});
		}
		e.target.value = null;
	};

	const handelUpdateBio = () => {
		axios
			.post(`/auth/update-user-bio`, {
				bio: bioText,
			})
			.then(({ data }) => {
				toast.success("Bio Updated Successfully");
				setEditBio(false);
				dispatch(setUserObject({ ...user, bio: bioText }));
			})
			.catch((e) => {
				console.log(e);
			});
	};

	const handelLocation = (btnState) => {
		if (!btnState) {
			const id = toast.loading("Updating location setting...");
			axios
				.post(`/auth/update-user-privacy`, {
					privacy_type: "enable_location",
					value: false,
				})
				.then(({ data }) => {
					setEnableLocation(false);
					dispatch(setUserObject(data.data));
					toast.update(id, {
						render: "Location disabled",
						type: "success",
						isLoading: false,
						autoClose: 2000,
					});
				})
				.catch((e) => {
					console.error(e);
					toast.update(id, {
						render: "Failed to update location setting",
						type: "error",
						isLoading: false,
						autoClose: 2000,
					});
				});
			return;
		}

		if (!navigator.geolocation) {
			return toast.warn("This browser does not support geolocation!");
		}

		const id = toast.loading("Detecting your location...");
		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude } = position.coords;
				axios
					.put(`/auth/update-user-details?userId=${userId}`, {
						latitude,
						longitude,
					})
					.then(() => {
						return axios.post(`/auth/update-user-privacy`, {
							privacy_type: "enable_location",
							value: true,
						});
					})
					.then(({ data }) => {
						setEnableLocation(true);
						dispatch(setUserObject(data.data));
						toast.update(id, {
							render: "Location enabled successfully",
							type: "success",
							isLoading: false,
							autoClose: 2000,
						});
					})
					.catch((e) => {
						console.error(e);
						toast.update(id, {
							render: "Failed to save location",
							type: "error",
							isLoading: false,
							autoClose: 2000,
						});
					});
			},
			(error) => {
				console.error(`Error getting geolocation: ${error.message}`);
				toast.update(id, {
					render: error.code === 1 ? "Location permission denied. Please allow location in browser settings." : "Unable to retrieve your location.",
					type: "error",
					isLoading: false,
					autoClose: 3000,
				});
				setEnableLocation(false);
			},
			{ enableHighAccuracy: true, timeout: 10000 }
		);
	};

	const handelLogout = () => {
		toast.success("Logout SuccessFully!!");
		if (users.length <= 1) {
			dispatch(logOut());
			navigate("/", { replace: true });
			if (users.length === 1) dispatch(removeUser(users[0].userId));
		} else {
			const data = users.find((obj) => userId === obj.userId);
			if (!data) {
				// If current user not found in list, just log out everything
				dispatch(logOut());
				navigate("/", { replace: true });
				return;
			}
			const index = users.indexOf(data);
			if (index === 0) {
				dispatch(switchAccount({ userId: users[1].userId, user: users[1].user, token: users[1].token }));
				dispatch(removeUser(data.userId));
			} else {
				dispatch(switchAccount({ userId: users[0].userId, user: users[0].user, token: users[0].token }));
				dispatch(removeUser(data.userId));
			}
		}
	};

	const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 1024);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	//Theme toggle
	const mode = useSelector((state) => state.theme.mode)
	console.log(mode)

	useEffect(() => {
		// Apply theme to <html> tag
		document.documentElement.setAttribute("data-theme", mode);
	}, [mode]);


	const profileImage =
		user?.ProfilePic && user.ProfilePic.trim() !== ""
			? user.ProfilePic
			: defaultProfileImg;

	return (
		<div className="App">
			<div className="MainContainer">
				<ScrollToTop />

				{/* <Header /> */}

				{addAccountPopup && <SwitchAccount {...{ setAddAccountPopup }} />}

				<div className={`${styles.Container} ${styles.Setting}`}>
					<div className={styles.LeftSettingContainer}>
						{isMobile ? null : (
							<h1 className={styles.SettingText}>Setting</h1>
						)}
						{/* {isMobile ? (
							<div className={styles.LeftContainerTop}>
								<h1 className={styles.SettingText}>Setting</h1>
								<img
									src={close}
									alt=""
									onClick={() => {
										navigate(-1);
									}}
								/>
							</div>
						) : (
							<h1 className={styles.SettingText}>Setting</h1>
						)} */}

						<h5 className={styles.AccountText}>Account</h5>

						<div className={styles.AccountDiv}>
							<div className={styles.AccountDivTop}>
								<div className={styles.ProfileImageContainer}>
									<div className={styles.ProfileImg}>
										{/* <img src={ImageURL ? ImageURL : ProfileImage1} alt="" /> */}
										<Avatar
											src={user?.ProfilePic}
											name={user?.displayName || user?.username}
											className={styles.profileAvatar}
										/>
									</div>
									<button
										className={styles.SelectImageBtn}
										type="button"
										onClick={() => ImageRef.current.click()}>
										<img src={CameraIcon} alt="" />
									</button>
									<input type="file" accept="image/*" ref={ImageRef} onChange={handelProfileImageChange} />
								</div>

								<div className={styles.NameContainer}>
									<h1>{user?.displayName || user?.username}</h1>
									<p>@{user?.GetstatedName}</p>
								</div>
							</div>

							<div className={styles.AccountDivBottom}>
								<div className={styles.TopSectionDiv}>
									<h1>Bio</h1>

									<button
										onClick={() => {
											if (editBio) {
												handelUpdateBio();
											} else {
												setEditBio(true);
												setBioText(user?.bio);
											}
										}}>
										{editBio ? "Save" : "Edit Bio"}
									</button>
								</div>

								<textarea
									placeholder="Tell us about yourself..."
									value={bioText}
									readOnly={!editBio}
									style={{ border: editBio ? "1px solid var(--pink1)" : "none" }}
									onChange={(e) => setBioText(e.target.value)}></textarea>

								<div className={styles.AccountDivBottomBottom}>
									<button type="button" className={styles.BusinessAccountLabel} onClick={() => toast.info("Coming Soon...")}>
										Change to business account
									</button>
									<button
										className={styles.activeButton2}
										type="button"
										onClick={() => navigate("/ChoseTemplate")}>
										Change Persona
									</button>
								</div>
							</div>
						</div>
						<button onClick={() => dispatch(toggleTheme())} className={styles.LogOutBtn}>
							Switch to {mode === "dark" ? "Light" : "Dark"} Mode
						</button>
						<div className={styles.ActiveContainer}>
							<h1 className={styles.ActiveText}>Active Status</h1>
							<p className={styles.ActiveTextP}>Your friends and contacts will see when you’re active </p>
							<div className={styles.SwitchContainer}>
								<Switch
									className={styles.switch}
									onChange={(e) => handelActiveSwitch(e)}
									checked={ActiveStatus}
									offColor={"#c4c4c4"}
									onColor={"#EFEFEF"}
									// offHandleColor={""}
									onHandleColor={"#A6539D"}
									uncheckedIcon={false}
									checkedIcon={false}
									activeBoxShadow={"0 0 0px 0px #909AFF"}
									height={isMobile ? 35 : 30}
									width={isMobile ? 70 : 64}
								/>
								<p className={styles.SwitchText}>Show my friends and contact when i’m active</p>
							</div>
						</div>

						{isMobile ? (
							<></>
						) : (
							<>
								<button type="button" className={styles.LogOutBtn} onClick={handelLogout}>
									Log Out
								</button>

								<Link className={styles.AddAccountText} onClick={() => setAddAccountPopup(true)}>
									Add another account
								</Link>
							</>
						)}
					</div>

					<div className={styles.RightSettingContainer}>
						<h1 className={styles.PreferenceText}>Preference</h1>

						<div className={styles.PreferenceContainer} onClick={() => toast.info("Comming Soon...")}>
							<div>
								<h3>Persona Insights</h3>
								<p>Watch your persona stats</p>
							</div>
							<RightArrow />
						</div>

						<div className={styles.PreferenceContainer} onClick={() => navigate("/Privacy")}>
							<div>
								<h3>Privacy</h3>
								<p>Manage your privacy</p>
							</div>
							<RightArrow />
						</div>

						<div className={styles.PreferenceContainer} onClick={() => navigate("/Security")}>
							<div>
								<h3>Security</h3>
								<p>Manage your security aand secure your profile</p>
							</div>
							<RightArrow />
						</div>

						<div
							className={styles.PreferenceContainer}
							onClick={() => {
								const el = document.getElementById("social-links-section");
								if (el) {
									el.scrollIntoView({ behavior: "smooth" });
								} else {
									navigate("/SocialLinks");
								}
							}}
						>
							<div>
								<h3>Social Links</h3>
								<p>Link your Instagram, Facebook, X, LinkedIn accounts</p>
							</div>
							<RightArrow />
						</div>

						<div className={styles.PreferenceContainerSwitch}>
							<h3 className={styles.h31}>Enable my location</h3>
							<Switch
								onChange={(checked) => handelLocation(checked)}
								checked={Boolean(EnableLocation)}
								offColor={"#EEEEEE"}
								onColor={"#EEEEEE"}
								offHandleColor={"#959595"}
								onHandleColor={"#A6539D"}
								uncheckedIcon={false}
								checkedIcon={false}
								activeBoxShadow={"0 0 0px 0px #909AFF"}
								height={isMobile ? 35 : 30}
								width={isMobile ? 70 : 64}
							/>
						</div>

						<div className={styles.PreferenceContainerSwitch}>
							<h3 className={styles.h32}>Play Sound When i get notification</h3>
							<Switch
								onChange={(checked) => handleNotificationSound(checked)}
								checked={Boolean(PlaySoundNotification)}
								offColor={"#EEEEEE"}
								onColor={"#EEEEEE"}
								offHandleColor={"#959595"}
								onHandleColor={"#A6539D"}
								uncheckedIcon={false}
								checkedIcon={false}
								activeBoxShadow={"0 0 0px 0px #909AFF"}
								height={isMobile ? 35 : 30}
								width={isMobile ? 70 : 64}
							/>
						</div>

						<div className={styles.PreferenceContainerSwitch}>
							<h3 className={styles.h33}>Enable Desktop notification</h3>
							<Switch
								onChange={(checked) => handleDesktopNotification(checked)}
								checked={Boolean(DesktopNotification)}
								offColor={"#EEEEEE"}
								onColor={"#EEEEEE"}
								offHandleColor={"#959595"}
								onHandleColor={"#A6539D"}
								uncheckedIcon={false}
								checkedIcon={false}
								activeBoxShadow={"0 0 0px 0px #909AFF"}
								height={isMobile ? 35 : 30}
								width={isMobile ? 70 : 64}
							/>
						</div>

						<div className={styles.BlockContainer}>
							<h1>Blocked Users</h1>
							<p>
								Once you blocked someone they will no longer to see things that you post on your feed , tag you ,
								invite you or start a conversation with you. However you can unblock them later if you want{" "}
							</p>
							<Link to="/BlockedUser">Manage blocked users</Link>
						</div>

						<div id="social-links-section" style={{ marginTop: "4rem", width: "100%" }}>
							<h1 className={styles.PreferenceText} style={{ marginTop: "2rem", marginBottom: "1.5rem" }}>
								Social Links
							</h1>
							<SocialLinksSection isEmbedded={true} />
						</div>

						{isMobile ? (
							<>
								<button type="button" className={styles.LogOutBtn} onClick={handelLogout}>
									Log Out
								</button>

								<Link className={styles.AddAccountText} onClick={() => setAddAccountPopup(true)}>
									Add another account
								</Link>
							</>
						) : (
							<></>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Setting;

